export const CONSENT_KEY = 'cookie_consent';
const OLD_CONSENT_KEY = 'cookieConsent';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
  }
}

function ensureDataLayer(): Record<string, unknown>[] {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function gtag(...args: unknown[]): void {
  ensureDataLayer().push(args as unknown as Record<string, unknown>);
}

/**
 * Migrate old consent key ('cookieConsent') to the new aligned key ('cookie_consent').
 * Prevents re-prompting existing users.
 */
export function migrateConsentKey(): void {
  const newValue = localStorage.getItem(CONSENT_KEY);
  if (newValue) return; // already migrated or set

  const oldValue = localStorage.getItem(OLD_CONSENT_KEY);
  if (oldValue) {
    localStorage.setItem(CONSENT_KEY, oldValue);
    localStorage.removeItem(OLD_CONSENT_KEY);
  }
}

/**
 * Get the current consent status from localStorage.
 */
export function getConsentStatus(): 'accepted' | 'declined' | null {
  const value = localStorage.getItem(CONSENT_KEY);
  if (value === 'accepted' || value === 'declined') return value;
  return null;
}

/**
 * Initialize Google Tag Manager with Consent Mode v2.
 * GTM loads immediately but analytics cookies are blocked until consent is granted.
 */
export function initGtm(containerId: string): void {
  // Don't initialize twice
  if (document.querySelector(`script[src*="gtm.js?id=${containerId}"]`)) {
    return;
  }

  migrateConsentKey();

  const dl = ensureDataLayer();

  // Set default consent state (denied until user accepts)
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });

  // If user previously consented, update immediately
  const status = getConsentStatus();
  if (status === 'accepted') {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      analytics_storage: 'granted',
    });
  }

  // Inject GTM script
  dl.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

/**
 * Update consent state and persist to localStorage.
 */
export function updateConsent(granted: boolean): void {
  localStorage.setItem(CONSENT_KEY, granted ? 'accepted' : 'declined');

  if (granted) {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      analytics_storage: 'granted',
    });
  } else {
    gtag('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
  }

  ensureDataLayer().push({ event: 'consent_update', consent_granted: granted });
}

/**
 * Push a custom event to the GTM dataLayer.
 */
export function pushEvent(
  event: string,
  data?: Record<string, unknown>,
): void {
  ensureDataLayer().push({ event, ...data });
}
